import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { LocalStorageService } from '../localStorage/local-storage.service';

/**
 * UserService - Servicio especializado para gestión de usuarios
 * Responsabilidad única: Obtener, almacenar y gestionar datos de usuario
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  private apiUrl = environment.apiURL;
  private user: UserData | null = null;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService
  ) { }

  /**
   * Obtiene los datos del usuario actual desde el API
   * @returns Observable con los datos del usuario
   */
  getUserData(): Observable<UserData> {
    const token = this.localStorageService.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    const url = this.apiUrl + 'users';
    
    return this.http.get<UserData>(url, { headers }).pipe(
      map((response: UserData) => {
        this.user = response;
        return response;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Almacena los datos del usuario en memoria y localStorage
   * @param userData Datos del usuario a almacenar
   */
  setUser(userData: UserData): void {
    this.user = userData;
    this.localStorageService.setItem('user', JSON.stringify(userData));
  }

  /**
   * Obtiene los datos del usuario almacenados
   * Primero busca en memoria, si no encuentra, busca en localStorage
   * @returns Datos del usuario o null si no existe
   */
  getUser(): UserData | null {
    if (!this.user) {
      const storedUser = this.localStorageService.getItem('user');
      this.user = storedUser ? JSON.parse(storedUser) : null;
    }
    return this.user;
  }

  /**
   * Obtiene el email del usuario del localStorage
   * @returns Email del usuario o null
   */
  getUserEmail(): string | null {
    return this.localStorageService.getItem('user_email');
  }

  /**
   * Limpia los datos del usuario de la memoria
   */
  clearUser(): void {
    this.user = null;
  }
}

/**
 * Interface para los datos del usuario
 */
export interface UserData {
  name?: string;
  email?: string;
  role?: string;
  account_type?: string;
  account_name?: string;
  account_reference?: string;
  user_reference?: string;
  [key: string]: any;
}
